// Intent: the owner's "Wspólne mieszkanko" fixed-costs sheet with snapshot
// columns — as a screen: list with due days, SUM hero, growth-over-time line
// ("widzę jak rosły opłaty"), paid-this-month checkmarks that write a BILLS
// expense into the budget, and the "poduszka" months-of-cover metric.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { TrendLine } from '@/components/money-charts';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import {
  costDaySuggestionOf,
  fixedCostsTotalGrOf,
  totalHistoryOf,
  useFixedCostsStore,
} from '@/store/fixed-costs-store';
import { savedGrOf, useGoalsStore } from '@/store/goals-store';
import { monthKeyOf } from '@/store/income-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatPln } from '@/utils/format';

function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

export default function FixedCostsScreen() {
  const { t } = useTranslation();
  const costs = useFixedCostsStore((s) => s.costs);
  const addCost = useFixedCostsStore((s) => s.addCost);
  const updateCost = useFixedCostsStore((s) => s.updateCost);
  const removeCost = useFixedCostsStore((s) => s.removeCost);
  const markPaid = useFixedCostsStore((s) => s.markPaid);
  const unmarkPaid = useFixedCostsStore((s) => s.unmarkPaid);
  const dismissDaySuggestion = useFixedCostsStore((s) => s.dismissDaySuggestion);
  const goals = useGoalsStore((s) => s.goals);
  const contributions = useGoalsStore((s) => s.contributions);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editAmountRaw, setEditAmountRaw] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [amountRaw, setAmountRaw] = useState('');
  const [dayRaw, setDayRaw] = useState('');
  const [addToBudget, setAddToBudget] = useState(true);

  const month = monthKeyOf(new Date());
  const active = costs.filter((c) => c.active);
  const totalGr = fixedCostsTotalGrOf(costs);
  const trend = totalHistoryOf(costs);
  // Day-change suggestions (A5): confirm-day drifted from dueDay for 3 months.
  const daySuggestions = active
    .map((c) => ({ cost: c, day: costDaySuggestionOf(c) }))
    .filter((x): x is { cost: (typeof active)[number]; day: number } => x.day !== null);

  // "Poduszka" — the sheet's "zapas na 6 miesięcy" translated to months of cover.
  const savingsGr = goals.reduce((s, g) => s + savedGrOf(g, contributions), 0);
  const coverMonths = totalGr > 0 ? Math.floor(savingsGr / totalGr) : null;

  const onAdd = async () => {
    const day = parseInt(dayRaw, 10);
    await addCost({
      name,
      amountGr: parseZl(amountRaw),
      dueDay: Number.isFinite(day) && day >= 1 && day <= 31 ? day : null,
      addToBudget,
    });
    setAdding(false);
    setName('');
    setAmountRaw('');
    setDayRaw('');
    setAddToBudget(true);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        >
          <ScreenHeader title={t('fixedCosts.title')} />

          {/* SUM hero + growth line. Tapping the amount opens the add form —
              owner: user tapped the "0 zł" total expecting to add a cost and
              didn't notice the button below. */}
          <Pressable accessibilityRole="button" accessibilityLabel={t('fixedCosts.add')} onPress={() => setAdding(true)}>
            <Card style={{ gap: spacing.sm }}>
              <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {t('fixedCosts.totalLabel')}
              </AppText>
              <AppText variant="headlineLg" color={colors.navy} style={{ fontSize: 34, lineHeight: 40 }}>
                {formatPln(totalGr)} <AppText variant="bodyMd">/ {t('fixedCosts.perMonth')}</AppText>
              </AppText>
              {totalGr === 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Ionicons name="add-circle-outline" size={16} color={colors.primary} />
                  <AppText variant="labelMd" color={colors.primary}>
                    {t('fixedCosts.tapToAdd')}
                  </AppText>
                </View>
              )}
              {trend.length >= 2 && (
                <>
                  <AppText variant="labelMd" color={colors.textSecondary}>
                    {t('fixedCosts.trendLabel')}
                  </AppText>
                  <TrendLine points={trend} />
                </>
              )}
            </Card>
          </Pressable>

          {/* Poduszka metric (the sheet's "zapas") */}
          {coverMonths !== null && (
            <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
              <Ionicons name="shield-checkmark-outline" size={26} color={colors.primary} />
              <View style={{ flex: 1, gap: 2 }}>
                <AppText variant="labelLg" color={colors.navy}>
                  {t('fixedCosts.cushionTitle')}
                </AppText>
                <AppText variant="bodyMd">
                  {t('fixedCosts.cushionBody', { count: coverMonths })}
                </AppText>
              </View>
            </Card>
          )}

          {/* Day-change suggestions (A5) — the app proposes, the human clicks. */}
          {daySuggestions.map(({ cost, day }) => (
            <Card key={`sugg-${cost.id}`} style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                <Ionicons name="bulb-outline" size={20} color={colors.navy} />
                <AppText variant="bodyLg" style={{ flex: 1 }}>
                  {t('fixedCosts.daySuggestion', { name: cost.name, day })}
                </AppText>
              </View>
              <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void updateCost(cost.id, { dueDay: day })}
                  style={{ backgroundColor: colors.primary, borderRadius: radius.full, paddingVertical: 8, paddingHorizontal: spacing.md }}
                >
                  <AppText variant="labelMd" color="#FFFFFF">
                    {t('fixedCosts.daySuggestionAccept', { day })}
                  </AppText>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void dismissDaySuggestion(cost.id, day)}
                  style={{ backgroundColor: colors.card, borderRadius: radius.full, paddingVertical: 8, paddingHorizontal: spacing.md }}
                >
                  <AppText variant="labelMd" color={colors.navy}>
                    {t('fixedCosts.daySuggestionKeep')}
                  </AppText>
                </Pressable>
              </View>
            </Card>
          ))}

          {/* Cost rows */}
          <Card style={{ padding: 0 }}>
            {active.length === 0 && (
              <AppText variant="bodyMd" style={{ padding: spacing.md }}>
                {t('fixedCosts.empty')}
              </AppText>
            )}
            {active.map((cost, idx) => {
              const expanded = expandedId === cost.id;
              const paid = cost.paidMonths.includes(month);
              return (
                <View key={cost.id} style={{ borderTopWidth: idx === 0 ? 0 : 1, borderTopColor: colors.divider }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, minHeight: 56 }}>
                    {/* paid toggle */}
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={paid ? t('fixedCosts.unmarkPaid') : t('fixedCosts.markPaid')}
                      onPress={() => (paid ? unmarkPaid(cost.id, month) : markPaid(cost.id, month))}
                      style={{ minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Ionicons
                        name={paid ? 'checkmark-circle' : 'ellipse-outline'}
                        size={26}
                        color={paid ? colors.primary : colors.outlineVariant}
                      />
                    </Pressable>
                    <Pressable
                      accessibilityRole="button"
                      onPress={() => {
                        setExpandedId(expanded ? null : cost.id);
                        setEditAmountRaw(String(Math.round(cost.amountGr / 100)));
                        setConfirmRemoveId(null);
                      }}
                      style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}
                    >
                      <View style={{ flex: 1, gap: 2 }}>
                        <AppText variant="labelLg">{cost.name}</AppText>
                        <AppText variant="labelMd">
                          {cost.dueDay
                            ? t('fixedCosts.dueDay', { day: cost.dueDay })
                            : t('fixedCosts.dueStart')}
                        </AppText>
                      </View>
                      <AppText variant="labelLg" color={colors.navy}>
                        {formatPln(cost.amountGr)}
                      </AppText>
                      <Ionicons name={expanded ? 'chevron-down' : 'chevron-forward'} size={18} color={colors.outlineVariant} />
                    </Pressable>
                  </View>
                  {expanded && (
                    <View style={{ padding: spacing.md, paddingTop: 0, gap: spacing.sm }}>
                      {/* amount history — the Excel snapshot columns */}
                      {cost.history.length > 0 && (
                        <View style={{ gap: 2 }}>
                          <AppText variant="labelMd" color={colors.textSecondary}>
                            {t('fixedCosts.historyLabel')}
                          </AppText>
                          {cost.history.map((h, i) => (
                            <AppText key={i} variant="bodyMd">
                              {h.fromIso.slice(0, 10)}: {formatPln(h.amountGr)}
                            </AppText>
                          ))}
                          <AppText variant="bodyMd" color={colors.navy}>
                            {t('fixedCosts.historyNow')}: {formatPln(cost.amountGr)}
                          </AppText>
                        </View>
                      )}
                      <TextField
                        label={t('fixedCosts.newAmountLabel')}
                        keyboardType="numeric"
                        value={editAmountRaw}
                        onChangeText={setEditAmountRaw}
                      />
                      <PrimaryButton
                        label={t('fixedCosts.saveAmount')}
                        onPress={async () => {
                          const gr = parseZl(editAmountRaw);
                          if (gr > 0) await updateCost(cost.id, { amountGr: gr });
                          setExpandedId(null);
                        }}
                      />
                      <Pressable
                        accessibilityRole="button"
                        onPress={() => {
                          if (confirmRemoveId === cost.id) {
                            void removeCost(cost.id);
                            setExpandedId(null);
                            setConfirmRemoveId(null);
                          } else {
                            setConfirmRemoveId(cost.id);
                          }
                        }}
                        style={{ minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                      >
                        <AppText variant="labelMd" color={colors.error}>
                          {confirmRemoveId === cost.id ? t('fixedCosts.removeConfirm') : t('fixedCosts.remove')}
                        </AppText>
                      </Pressable>
                    </View>
                  )}
                </View>
              );
            })}
          </Card>

          {/* Add form */}
          {adding ? (
            <Card style={{ gap: spacing.sm }}>
              <TextField
                label={t('fixedCosts.nameLabel')}
                placeholder={t('fixedCosts.namePlaceholder')}
                value={name}
                onChangeText={setName}
              />
              <TextField
                label={t('fixedCosts.amountLabel')}
                placeholder="np. 830"
                keyboardType="numeric"
                value={amountRaw}
                onChangeText={setAmountRaw}
              />
              <TextField
                label={t('fixedCosts.dayLabel')}
                placeholder="np. 10"
                keyboardType="numeric"
                value={dayRaw}
                onChangeText={(v) => setDayRaw(v.replace(/[^\d]/g, '').slice(0, 2))}
              />
              <AppText variant="labelMd" color={colors.textSecondary}>
                {t('fixedCosts.dayHint')}
              </AppText>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 44 }}>
                <AppText variant="bodyLg" style={{ flex: 1 }}>
                  {t('fixedCosts.addToBudget')}
                </AppText>
                <Switch
                  value={addToBudget}
                  onValueChange={setAddToBudget}
                  trackColor={{ false: colors.disabledBg, true: colors.primary }}
                  thumbColor="#FFFFFF"
                />
              </View>
              <PrimaryButton
                label={t('fixedCosts.add')}
                disabled={!name.trim() || parseZl(amountRaw) <= 0}
                onPress={onAdd}
              />
              <PrimaryButton label={t('common.cancel')} variant="secondary" onPress={() => setAdding(false)} />
            </Card>
          ) : (
            <PrimaryButton label={`+ ${t('fixedCosts.add')}`} onPress={() => setAdding(true)} />
          )}

          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
            <Ionicons name="information-circle-outline" size={16} color={colors.outline} style={{ marginTop: 2 }} />
            <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
              {t('fixedCosts.footerInfo')}
            </AppText>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
